import { Component, ElementRef, inject, input, signal, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { ImageGeneratorComponent } from '@components/image-generator/image-generator.component';
import { Artist, Maybe, RoleArtist } from '@shared/graphql/api.generated';
import { WheelControls } from '@shared/helper/keen-slider-plugins';
import { artistSearchResultSP } from '@shared/helper/responsive-sizes';
import { IDisplayedCast } from '@shared/models/cast.interface';
import { GeneralService } from '@shared/services/general.service';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';

@Component({
  selector: 'app-product-cast',
  imports: [ImageGeneratorComponent, MatIconModule, RouterLink],
  templateUrl: './product-cast.component.html',
  styleUrls: ['./product-cast.component.scss'],
})
export class ProductCastComponent {
  generalService = inject(GeneralService);

  @ViewChild('sliderRef') sliderRef!: ElementRef<HTMLElement>;
  Casts = input<Maybe<RoleArtist>[]>([]);
  displayedCasts = signal<IDisplayedCast[]>([]);
  desktopSlider = signal<KeenSliderInstance | undefined>(undefined);
  currentSlide = signal(0);
  showRow = signal(false);
  perViewCounter = signal(10);
  artistSizes = artistSearchResultSP;

  ngOnChanges() {
    this.handleCastsData();
    if (!this.generalService.isSmallScreen()) {
      setTimeout(() => {
        this.handleSlider();
      });
    } else {
      this.showRow.set(true);
    }
  }

  private handleSlider() {
    if (this.generalService.isBrowserOnly) {
      const width = window.innerWidth;
      if (width > 1200) {
        this.perViewCounter.set(10);
      } else if (width <= 1200 && width > 992) {
        this.perViewCounter.set(8);
      } else {
        this.perViewCounter.set(6);
      }
      this.desktopSlider.set(
        new KeenSlider(
          this.sliderRef.nativeElement,
          {
            initial: this.currentSlide(),
            loop: false,
            rtl: true,
            drag: true,
            renderMode: 'performance',
            breakpoints: {
              '(max-width: 1200px)': {
                slides: {
                  perView: 8,
                  spacing: 32,
                },
              },
              '(max-width: 992px)': {
                slides: {
                  perView: 6,
                  spacing: 32,
                },
              },
            },
            slides: {
              perView: 10,
              spacing: 32,
            },
            slideChanged: (s) => {
              this.currentSlide.set(s.track.details.rel);
            },
            created: () => {
              this.showRow.set(true);
            },
          },
          [WheelControls],
        ),
      );
    }
  }

  private handleCastsData() {
    const handleAlreadyExistArtist = (artist: Artist, el: RoleArtist) => {
      const findCast = this.displayedCasts().filter((item) => item.artist?.id === artist?.id);
      if (findCast.length) {
        findCast[0].roles.push(el.name);
      } else {
        this.displayedCasts.update((items) => [
          ...items,
          {
            artist,
            roles: [el.name],
          },
        ]);
      }
    };

    const handleFirstElement = (artist: Artist, el: RoleArtist) => {
      this.displayedCasts.update((items) => [
        ...items,
        {
          artist,
          roles: [el.name],
        },
      ]);
    };

    const handleArtists = (element: RoleArtist) => {
      if (element.artists?.length) {
        for (const artist of element.artists) {
          if (this.displayedCasts().length) {
            handleAlreadyExistArtist(artist, element);
          } else {
            handleFirstElement(artist, element);
          }
        }
      }
    };

    if (this.Casts().length) {
      for (const element of this.Casts()) {
        handleArtists(element!);
      }
    }
  }

  ngOnDestroy() {
    this.desktopSlider()?.destroy();
  }

  nextSlide() {
    this.desktopSlider()?.moveToIdx(this.currentSlide() + 1);
  }

  prevSlide() {
    this.desktopSlider()?.moveToIdx(this.currentSlide() - 1);
  }
}
